import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Mail, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { GlassCard } from "@/components/common/GlassCard";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { useEmailConnectReturn } from "@/hooks/useEmailConnectReturn";
import {
  connectImap,
  disconnectEmail,
  getEmailStatus,
  startMicrosoftEmailConnect,
  type EmailConnectionStatus,
  type EmailProvider,
} from "@/lib/api/email";
import { APPS_HOME_PATH } from "@/lib/apps";

export function EmailIntegrationPage() {
  const [status, setStatus] = useState<EmailConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnectProvider, setDisconnectProvider] = useState<EmailProvider>("microsoft");
  const [disconnecting, setDisconnecting] = useState(false);
  const [imapForm, setImapForm] = useState({
    host: "",
    port: "993",
    secure: true,
    username: "",
    password: "",
  });

  const refreshStatus = useCallback(async () => {
    setLoading(true);
    try {
      setStatus(await getEmailStatus());
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to load email integration");
    } finally {
      setLoading(false);
    }
  }, []);

  useEmailConnectReturn({
    onConnected: () => {
      setActionMessage("Microsoft Outlook connected successfully.");
      void refreshStatus();
    },
    onError: setActionError,
  });

  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  async function handleMicrosoftConnect() {
    setActionError(null);
    try {
      const { url } = await startMicrosoftEmailConnect("/settings/email");
      window.location.href = url;
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to start Microsoft connection");
    }
  }

  async function handleImapConnect() {
    setActionError(null);
    setActionMessage(null);
    try {
      await connectImap({
        ...imapForm,
        port: Number(imapForm.port),
      });
      setActionMessage("IMAP mailbox connected successfully.");
      setImapForm((current) => ({ ...current, password: "" }));
      await refreshStatus();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to connect IMAP mailbox");
    }
  }

  async function handleDisconnect() {
    setDisconnecting(true);
    setActionError(null);
    try {
      await disconnectEmail(disconnectProvider);
      setDisconnectOpen(false);
      setActionMessage("Email mailbox disconnected.");
      await refreshStatus();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to disconnect email mailbox");
    } finally {
      setDisconnecting(false);
    }
  }

  if (loading && !status) {
    return (
      <section className="flex flex-1 items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-violet-500" />
      </section>
    );
  }

  return (
    <section className="space-y-6 p-6 md:p-8">
      <div>
        <Link to={APPS_HOME_PATH} className="inline-flex items-center gap-1.5 text-sm text-[var(--lm-muted)] hover:text-[var(--lm-foreground-strong)]">
          <ArrowLeft className="h-4 w-4" />
          Back to apps
        </Link>
        <p className="mt-4 text-xs uppercase tracking-[0.14em] text-violet-500">Settings</p>
        <h1 className="mt-1 text-2xl font-semibold text-[var(--lm-foreground-strong)]">Email inbox</h1>
        <p className="mt-2 max-w-2xl text-sm text-[var(--lm-muted)]">
          Connect a personal Outlook account or a read-only IMAP mailbox to import email chains into reviews and drafts.
        </p>
      </div>
      {actionMessage ? <p className="text-sm text-emerald-600">{actionMessage}</p> : null}
      {actionError ? <p className="text-sm text-red-600">{actionError}</p> : null}

      <GlassCard className="space-y-5 border p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-violet-500/10 p-3 text-violet-500"><Mail className="h-5 w-5" /></div>
            <div>
              <h2 className="text-lg font-semibold text-[var(--lm-foreground-strong)]">Microsoft Outlook</h2>
              <p className="mt-1 text-sm text-[var(--lm-muted)]">
                {status?.microsoft.connected
                  ? `Connected as ${status.microsoft.connectedAccountEmail ?? "your account"}`
                  : status?.microsoft.reconnectRequired
                    ? "Authorization expired. Reconnect to continue."
                    : "Read-only Mail.Read access. Sending is not requested."}
              </p>
            </div>
          </div>
          {status?.microsoft.connected ? (
            <Button
              variant="subtle"
              size="sm"
              onClick={() => {
                setDisconnectProvider("microsoft");
                setDisconnectOpen(true);
              }}
            >
              Disconnect
            </Button>
          ) : (
            <Button variant="accent" onClick={() => void handleMicrosoftConnect()} disabled={!status?.microsoft.enabled}>
              {status?.microsoft.reconnectRequired ? "Reconnect Outlook" : "Connect Outlook"}
            </Button>
          )}
        </div>
      </GlassCard>

      <GlassCard className="space-y-5 border p-6">
        <div>
          <h2 className="text-lg font-semibold text-[var(--lm-foreground-strong)]">Generic IMAP</h2>
          <p className="mt-1 text-sm text-[var(--lm-muted)]">Credentials are encrypted and used only for read-only mailbox access.</p>
        </div>
        {status?.imap.connected ? (
          <div className="flex items-center justify-between rounded-lg border border-emerald-400/30 bg-emerald-500/5 p-4">
            <p className="text-sm text-emerald-700">Connected as {status.imap.connectedAccountEmail}</p>
            <Button
              variant="subtle"
              size="sm"
              onClick={() => {
                setDisconnectProvider("imap");
                setDisconnectOpen(true);
              }}
            >
              Disconnect
            </Button>
          </div>
        ) : (
          <form
            className="grid gap-3 sm:grid-cols-2"
            onSubmit={(event) => {
              event.preventDefault();
              void handleImapConnect();
            }}
          >
            <input required placeholder="IMAP host" value={imapForm.host} onChange={(event) => setImapForm({ ...imapForm, host: event.target.value })} className="h-10 rounded-md border bg-transparent px-3 text-sm" />
            <input required type="number" min="1" max="65535" placeholder="Port" value={imapForm.port} onChange={(event) => setImapForm({ ...imapForm, port: event.target.value })} className="h-10 rounded-md border bg-transparent px-3 text-sm" />
            <input required placeholder="Username / email" value={imapForm.username} onChange={(event) => setImapForm({ ...imapForm, username: event.target.value })} className="h-10 rounded-md border bg-transparent px-3 text-sm" />
            <input required type="password" placeholder="Password or app password" value={imapForm.password} onChange={(event) => setImapForm({ ...imapForm, password: event.target.value })} className="h-10 rounded-md border bg-transparent px-3 text-sm" />
            <label className="flex items-center gap-2 text-sm text-[var(--lm-muted)]">
              <input type="checkbox" checked={imapForm.secure} onChange={(event) => setImapForm({ ...imapForm, secure: event.target.checked })} />
              TLS / IMAPS
            </label>
            <Button type="submit" variant="default" disabled={!status?.imap.enabled}>Test and connect IMAP</Button>
          </form>
        )}
      </GlassCard>

      <ConfirmDialog
        isOpen={disconnectOpen}
        onClose={() => setDisconnectOpen(false)}
        onConfirm={() => void handleDisconnect()}
        title={`Disconnect ${disconnectProvider === "imap" ? "IMAP" : "Outlook"}`}
        description={`Disconnect this ${disconnectProvider === "imap" ? "IMAP mailbox" : "Outlook account"}? The other email provider will remain connected.`}
        confirmLabel="Disconnect"
        variant="destructive"
        isLoading={disconnecting}
      />
    </section>
  );
}

